import React, { useEffect } from 'react';
import { faPlay, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import { FormToggle, NotificationDispatch, TextField } from '@tryhackme/thm-ui-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CSSTransition } from 'react-transition-group';
import { Controller, useForm, useWatch } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { SettingCard } from '../setting-card';
import { UpdateRoomConfirmModal } from '../update-room-confirm-modal';
import { DiscardChangesModal } from '../discard-changes-modal';
import { UnSavedWarnModal } from '../unsaved-warn-modal';
import { useUpdateRoomDesignMutation } from '../../manage-room.slice';
import { StyledRowDescription, StyledRowHeading } from '../form-row/form-row.styles';
import { StyledBottomAdornment } from '../design-tab/design-tab.styles';
import { ManageRoomDetail } from '../../manage-room.types';
import { InputContainer, StyledButtonContainer, StyledFormToggleContainer, StyledContainer, InputWrapper, ClearButton, StyledRoomPreviewVideo, TransitionStyledSectionContainer, StyledSectionContainer } from './video-tab.styles';
import { updateRoomVideoValidations, VideoFormValues } from './video-tab.constants';
export const VideoTab = ({
  roomDetails
}: {
  roomDetails: ManageRoomDetail;
}) => {
  const [updateDesign, {
    isLoading
  }] = useUpdateRoomDesignMutation();
  const [openUpdateModal, setOpenUpdateModal] = React.useState<boolean>(false);
  const defaultValues = {
    videoActive: roomDetails?.video.active ?? true,
    videoURL: roomDetails?.video.url ?? '',
    videoTitle: roomDetails?.video.title ?? ''
  };
  const {
    register,
    formState: {
      isDirty,
      errors
    },
    reset,
    setValue,
    control,
    handleSubmit
  } = useForm<VideoFormValues>({
    mode: 'onChange',
    resolver: yupResolver(updateRoomVideoValidations),
    defaultValues
  });
  const videoUrlValue = useWatch({
    name: 'videoURL',
    control
  });
  useEffect(() => {
    if (roomDetails?.video.active !== undefined) {
      setValue('videoActive', roomDetails.video.active, {
        shouldDirty: false // Don't mark as dirty since this is external update
      });
    }
  }, [roomDetails?.video.active, setValue]);
  const handleDiscardChanges = () => {
    reset();
  };
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setValue('videoURL', url, {
      shouldDirty: true,
      shouldValidate: true
    });
  };
  const handleClear = () => {
    setValue('videoURL', '', {
      shouldDirty: true
    });
  };
  const handleUpdateRoomVideo = async (values: {
    videoActive: boolean;
    videoURL: string;
    videoTitle: string;
  }): Promise<void> => {
    try {
      const {
        videoActive,
        videoURL
      } = values;
      const payload = {
        avatar: roomDetails?.imageURL,
        banner: roomDetails?.headerImage,
        uploadedFileName: '',
        videoActive,
        videoURL
      };
      await updateDesign({
        roomCode: roomDetails?.code,
        payload
      }).unwrap();
      NotificationDispatch('success', 'Room updated successfully.');
      reset(values); // Reset the form to remove dirty state
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        if (error.status === 400) {
          NotificationDispatch('error', error.data.message);
        } else {
          NotificationDispatch('error', 'Uh-oh. There was an error while updating your room video. Please try again.');
        }
      }
    } finally {
      setOpenUpdateModal(false);
    }
  };
  return <>
      <SettingCard title="Video" icon={faPlay} data-sentry-element="SettingCard" data-sentry-source-file="video-tab.tsx">
        <StyledSectionContainer data-sentry-element="StyledSectionContainer" data-sentry-source-file="video-tab.tsx">
          <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="video-tab.tsx">
            <Controller name="videoActive" control={control} defaultValue={defaultValues.videoActive} render={({
            field: {
              onChange,
              value
            }
          }) => <StyledFormToggleContainer>
                  <StyledRowHeading id="video-embed-toggle">Embed room video </StyledRowHeading>
                  <FormToggle checked={value} aria-label="Toggle video embedding" aria-labelledby="video-embed-toggle" onCheckedChange={checked => onChange(checked, {
              shouldDirty: true
            })} />
                </StyledFormToggleContainer>} data-sentry-element="Controller" data-sentry-source-file="video-tab.tsx" />
            <StyledRowDescription data-sentry-element="StyledRowDescription" data-sentry-source-file="video-tab.tsx">
              Add a video to your room by providing a valid URL. The video will be embedded directly into your room to
              enhance the learning experience. Supported platforms include YouTube and Vimeo. Ensure the video is
              publicly accessible or unlisted for it to display correctly & set a date that it was added for visibility.
            </StyledRowDescription>
          </StyledContainer>
        </StyledSectionContainer>
        <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="video-tab.tsx">
          <InputWrapper data-sentry-element="InputWrapper" data-sentry-source-file="video-tab.tsx">
            <InputContainer data-sentry-element="InputContainer" data-sentry-source-file="video-tab.tsx">
              <TextField data-testid="video-url-input" id="videoURL" variant="input" type="text" label="Video URL" disabled={!useWatch({
              name: 'videoActive',
              control
            })} placeholder="Enter URL or choose a file..." {...register('videoURL')} onChange={handleUrlChange} rightAdornment={videoUrlValue ? <ClearButton onClick={handleClear} data-testid="video-url-clear-button">
                      <FontAwesomeIcon icon={faXmarkCircle} />
                    </ClearButton> : <span />} bottomAdornment={<StyledBottomAdornment>Enter the URL of your video</StyledBottomAdornment>} isError={!!errors?.videoURL} errorMessage={errors?.videoURL?.message} data-sentry-element="TextField" data-sentry-source-file="video-tab.tsx" />
            </InputContainer>
          </InputWrapper>
        </StyledContainer>

        {!errors?.videoURL && videoUrlValue !== '' && <CSSTransition in={!!videoUrlValue} timeout={1000} classNames="video-preview" unmountOnExit>
            <TransitionStyledSectionContainer>
              <StyledContainer>
                <StyledRowHeading>Preview </StyledRowHeading>
              </StyledContainer>
              <StyledContainer>
                <StyledRoomPreviewVideo url={videoUrlValue} height="49rem" controls />
              </StyledContainer>
            </TransitionStyledSectionContainer>
          </CSSTransition>}
      </SettingCard>
      <StyledButtonContainer data-sentry-element="StyledButtonContainer" data-sentry-source-file="video-tab.tsx">
        {isDirty && <DiscardChangesModal handleActionButton={handleDiscardChanges} />}

        <UpdateRoomConfirmModal open={openUpdateModal} openChange={setOpenUpdateModal} isRoomCodeChanged={false} isUpdatingRoom={isLoading} isUpdateButtonDisabled={!!errors?.videoURL || !isDirty}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleUpdateRoom={handleSubmit(handleUpdateRoomVideo)} data-sentry-element="UpdateRoomConfirmModal" data-sentry-source-file="video-tab.tsx" />
      </StyledButtonContainer>
      <UnSavedWarnModal handleDiscardChanges={handleDiscardChanges}
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleActionButton={handleSubmit(handleUpdateRoomVideo)} isSavingChanges={isLoading} data-sentry-element="UnSavedWarnModal" data-sentry-source-file="video-tab.tsx" />
    </>;
};